import React, { useState, useEffect } from "react";
import API from "../services/api";
import "./MatchStandingsTab.css";

const MatchStandingsTab = ({ matchId }) => {
  const [standings, setStandings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStandings = async (id) => {
      try {
        setLoading(true);
        const url = `/matches/${id}/standings`;
        const response = await API.get(url);
        setStandings(response.data);
        setError(null);
      } catch (err) {
        console.error("❌ [StandingsTab] Erreur:", err);
        setError(
          err.response?.data?.message || "Classement indisponible pour ce match."
        );
      } finally {
        setLoading(false);
      }
    };

    if (matchId) {
      fetchStandings(matchId);
    }
  }, [matchId]);

  if (loading) {
    return (
      <div className="standings-loading">
        <div className="standings-spinner"></div>
        <span>Analyse du classement en cours...</span>
      </div>
    );
  }

  if (error) return <div className="standings-error">⚠️ {error}</div>;
  if (!standings) return <div className="standings-error">Aucune donnée de classement trouvée.</div>;

  const { home, away } = standings;

  // Fonction utilitaire pour générer les petits badges de forme (ex: V N D V V)
  const renderForm = (formString) => {
    if (!formString) return <span className="no-form">-</span>;
    return (
      <div className="form-badges">
        {formString.split("").map((letter, index) => {
          const l = letter.toUpperCase();
          let statusClass = "form-d"; // Défaite par défaut
          if (l === "W" || l === "V") statusClass = "form-w"; // Victoire
          if (l === "D" || l === "N") statusClass = "form-drew"; // Nul
          return (
            <span key={index} className={`form-badge ${statusClass}`}>
              {l === "W" ? "V" : l === "D" ? "N" : l}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div className="standings-wrapper">
      <h3 className="standings-section-title">📊 Analyse des Positions</h3>

      {/* Vue d'ensemble sous forme de cartes d'équipe */}
      <div className="standings-overview-cards">
        <div className="team-summary-card home-card">
          <span className="card-role">🏠 Domicile</span>
          <h4 className="card-team-name">{home?.teamName || "Équipe Domicile"}</h4>
          <div className="card-main-stats">
            <div className="stat-pill rank">Rang <span>#{home?.position || "-"}</span></div>
            <div className="stat-pill points"><span>{home?.points ?? "-"}</span> pts</div>
          </div>
          <div className="card-form">
            <span className="label">Forme :</span>
            {renderForm(home?.form || "VVNVV")} {/* Fallback visuel s'il n'y a pas de data de forme */}
          </div>
        </div>

        <div className="team-summary-card away-card">
          <span className="card-role">🚀 Extérieur</span>
          <h4 className="card-team-name">{away?.teamName || "Équipe Extérieur"}</h4>
          <div className="card-main-stats">
            <div className="stat-pill rank">Rang <span>#{away?.position || "-"}</span></div>
            <div className="stat-pill points"><span>{away?.points ?? "-"}</span> pts</div>
          </div>
          <div className="card-form">
            <span className="label">Forme :</span>
            {renderForm(away?.form || "DDNDV")}
          </div>
        </div>
      </div>

      {/* Tableau comparatif ultra-pro */}
      <div className="standings-table-container">
        <table className="standings-pro-table">
          <thead>
            <tr>
              <th>Indicateurs de Performance</th>
              <th className="text-center">🏠 Domicile</th>
              <th className="text-center">🚀 Extérieur</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Matchs Joués</strong></td>
              <td className="text-center font-semibold text-muted">{home?.played || 0}</td>
              <td className="text-center font-semibold text-muted">{away?.played || 0}</td>
            </tr>
            <tr className="highlight-row">
              <td><strong>Victoires</strong></td>
              <td className="text-center text-success font-bold">{home?.wins || 0}</td>
              <td className="text-center text-success font-bold">{away?.wins || 0}</td>
            </tr>
            <tr>
              <td><strong>Matchs Nuls</strong></td>
              <td className="text-center text-warning">{home?.draws || 0}</td>
              <td className="text-center text-warning">{away?.draws || 0}</td>
            </tr>
            <tr>
              <td><strong>Défaites</strong></td>
              <td className="text-center text-danger">{home?.losses || 0}</td>
              <td className="text-center text-danger">{away?.losses || 0}</td>
            </tr>
            {/* Lignes de statistiques additionnelles si votre backend les fournit un jour */}
            {(home?.goalsFor !== undefined || away?.goalsFor !== undefined) && (
              <>
                <tr className="divider-row"><td colSpan="3"></td></tr>
                <tr>
                  <td>Buts Marqués / Encaissés</td>
                  <td className="text-center">{home?.goalsFor || 0} : {home?.goalsAgainst || 0}</td>
                  <td className="text-center">{away?.goalsFor || 0} : {away?.goalsAgainst || 0}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MatchStandingsTab;